import { Link } from "react-router-dom";
import { ArrowUpRight, Globe2, LifeBuoy, RefreshCcw, Wallet, Radar } from "lucide-react";
import { useLanguage } from "../i18n";
import { usePageMeta } from "../hooks/usePageMeta";
import { Reveal } from "../components/Reveal";

export default function Business() {
  usePageMeta("biz.hero", "biz.sub");
  const { t } = useLanguage();

  const features = [
    [<Wallet key="1" className="w-6 h-6" />, t("biz.f1t"), t("biz.f1d")],
    [<RefreshCcw key="2" className="w-6 h-6" />, t("biz.f2t"), t("biz.f2d")],
    [<Globe2 key="3" className="w-6 h-6" />, t("biz.f3t"), t("biz.f3d")],
    [<Radar key="4" className="w-6 h-6" />, t("biz.f4t"), t("biz.f4d")],
  ];

  return (
    <div data-testid="business-page" className="px-4 sm:px-6 lg:px-12 max-w-5xl mx-auto py-14 sm:py-20">
      <Reveal>
        <div className="text-center max-w-2xl mx-auto">
          <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-4">{t("biz.nav")}</div>
          <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-6xl text-slate-900 text-balance">{t("biz.hero")}</h1>
          <p className="mt-5 text-lg text-slate-600">{t("biz.sub")}</p>
        </div>
      </Reveal>

      <div className="mt-16 grid md:grid-cols-2 gap-5">
        {features.map(([icon, title, text], i) => (
          <Reveal key={title} delay={i * 0.08}>
            <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-8 h-full hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-100 transition-all duration-200">
              <div className="flex items-center justify-between mb-8">
                <span className="w-12 h-12 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] flex items-center justify-center">{icon}</span>
                <span className="font-mono-t text-xs font-bold text-slate-300">0{i + 1}</span>
              </div>
              <h3 className="font-display font-bold text-xl text-slate-900 mb-3">{title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-16">
        <div className="rounded-3xl bg-[#1E293B] p-8 sm:p-12" data-testid="business-invoicing">
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-white mb-2">{t("biz.invT")}</h2>
          <p className="text-sm text-slate-400 mb-8 max-w-2xl">{t("biz.invSub")}</p>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              ["biz.inv1t", "biz.inv1d"],
              ["biz.inv2t", "biz.inv2d"],
              ["biz.inv3t", "biz.inv3d"],
            ].map(([tk, dk], i) => (
              <div key={tk} className="rounded-2xl bg-white/5 border border-white/10 p-6">
                <div className="font-mono-t text-xs font-bold text-[#FF6B35] mb-3">0{i + 1}</div>
                <h3 className="font-display font-bold text-lg text-white mb-2">{t(tk)}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{t(dk)}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      <Reveal className="mt-16">
        <div className="rounded-3xl bg-[#E6F7F5] p-8 sm:p-12 text-center">
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 mb-6">{t("biz.cta")}</h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/account" data-testid="business-cta-account"
              className="inline-flex items-center gap-2 rounded-full bg-[#FF6B35] text-white px-8 py-3.5 font-mono-t text-xs font-bold uppercase tracking-widest hover:bg-[#E8551F] hover:scale-[1.02] transition-all shadow-lg shadow-orange-200">
              {t("biz.cta")} <ArrowUpRight className="w-4 h-4" />
            </Link>
            <button data-testid="business-cta-contact"
              onClick={() => window.dispatchEvent(new Event("atlas-open-chat"))}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-8 py-3.5 font-mono-t text-xs font-bold uppercase tracking-widest text-slate-700 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
              <LifeBuoy className="w-4 h-4" /> {t("biz.contact")}
            </button>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
